import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ErrorFlash } from "../components/ErrorFlash";
import { FormFieldLabel } from "../components/FormFieldLabel";
import { PasswordInput } from "../components/PasswordInput";
import { PhoneInput } from "../components/PhoneInput";
import { PrototypeBanner, PublicNav } from "../components/ui";
import { pathKeepingDebug, publicSupabaseConfig } from "../domain/env";
import { browserSupabase } from "../domain/supabaseBrowser";
import {
  SIGN_UP_PASSWORD_MIN,
  signUpWithPassword,
} from "../domain/supabaseAuth";
import { useAuth } from "../state/AuthProvider";
import { useLocale } from "../state/i18n";

type SignupNotice =
  | { kind: "confirm_email"; email: string }
  | { kind: "signed_in"; email: string; continueTo: string };

export function SignUp() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { adoptHostedSession, pathForAccount, signUp } = useAuth();
  const { t } = useLocale();
  const supabaseReady = publicSupabaseConfig() !== null;
  const signInPath = pathKeepingDebug("/signin", `?${params.toString()}`);
  const [given, setGiven] = useState("");
  const [family, setFamily] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<SignupNotice | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const input = { given, family, email, phone, password, confirmPassword };
    if (supabaseReady) {
      const result = await signUpWithPassword(browserSupabase(), input, {
        emailRedirectTo: `${window.location.origin}/signin`,
      });
      if (!result.ok) {
        setBusy(false);
        setError(result.error);
        return;
      }
      if (result.status === "signed_in") {
        const continued = await adoptHostedSession();
        setBusy(false);
        setNotice({
          kind: "signed_in",
          email: result.email,
          continueTo: continued.ok ? pathForAccount(continued.account) : "/register",
        });
        return;
      }
      setBusy(false);
      setNotice({ kind: "confirm_email", email: result.email });
      return;
    }

    const result = await signUp(input);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate(pathForAccount(result.account));
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <PublicNav />
        <div className="auth-panel">
          <p className="meta">{t("signup.meta")}</p>
          {notice?.kind === "confirm_email" ? (
            <>
              <h1>{t("signup.confirmTitle")}</h1>
              <p className="lede" role="status">
                {t("signup.confirmBody", { email: notice.email })}
              </p>
              <div className="actions">
                <Link className="btn" to={signInPath}>
                  {t("signup.haveAccount")}
                </Link>
              </div>
            </>
          ) : null}
          {notice?.kind === "signed_in" ? (
            <>
              <h1>{t("signup.signedInTitle")}</h1>
              <p className="lede" role="status">
                {t("signup.signedInBody", { email: notice.email })}
              </p>
              <div className="actions">
                <Link className="btn" to={notice.continueTo}>
                  {t("signup.continue")}
                </Link>
              </div>
            </>
          ) : null}
          {notice ? null : (
            <>
              <h1>{t("signup.title")}</h1>
              <p className="lede">{t("signup.lede")}</p>

              <form className="stack" onSubmit={onSubmit}>
                <div className="grid-2">
                  <div className="field">
                    <label htmlFor="given">{t("signup.given")}</label>
                    <input
                      id="given"
                      type="text"
                      autoComplete="given-name"
                      value={given}
                      onChange={(e) => setGiven(e.target.value)}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="family">{t("signup.family")}</label>
                    <input
                      id="family"
                      type="text"
                      autoComplete="family-name"
                      value={family}
                      onChange={(e) => setFamily(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="email">{t("signup.email")}</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="field">
                  <FormFieldLabel htmlFor="signup-phone" required>
                    {t("signup.phone")}
                  </FormFieldLabel>
                  <PhoneInput
                    id="signup-phone"
                    value={phone}
                    onChange={setPhone}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="password">{t("signup.password")}</label>
                  <PasswordInput
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={setPassword}
                    required
                  />
                  {supabaseReady ? (
                    <p className="anno">
                      {t("signup.passwordRule", {
                        count: String(SIGN_UP_PASSWORD_MIN),
                      })}
                    </p>
                  ) : null}
                </div>
                <div className="field">
                  <label htmlFor="confirm-password">{t("signup.confirm")}</label>
                  <PasswordInput
                    id="confirm-password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    required
                  />
                </div>
                {error ? <ErrorFlash key={error} message={error} /> : null}
                <div className="actions">
                  <button type="submit" disabled={busy}>
                    {busy ? t("signup.busy") : t("signup.submit")}
                  </button>
                  <Link className="btn ghost" to={signInPath}>
                    {t("signup.haveAccount")}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
